<script setup lang="ts">
import {inject} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import Rank from "@/core/interfaces/Rank";
import {TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";
import {UNKNOWN_ROLE_COLOR, formatHours, jobRoleForRank, jobRoleOption} from "@/team/planning";
import {teamApi} from "@/container";
import AddTeamMemberButton from "@/team/components/AddTeamMemberButton.vue";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/core/components/ui/dropdown-menu";
import {EllipsisVertical, UserRoundX} from "@lucide/vue";
import {pluralize} from "@/core/filters/Pluralize";

const {team, schedules} = defineProps<{
    team: LegacyAccess[];
    schedules: Map<number, TeamMemberSchedule>;
    selectedId: number | null;
    ranks: Rank[];
    operatorCodeOptions: { label: string; value: string }[];
}>();

const emits = defineEmits(['select', 'refresh', 'schedule-updated', 'on-remove-operator-code']);

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

const initials = (member: LegacyAccess): string => {
    const name = member.user?.name ?? '';

    return name.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase() || '?';
};

const roleColor = (member: LegacyAccess): string => {
    const role = schedules.get(member.id)?.job_role ?? jobRoleForRank(member.rankId);

    return jobRoleOption(role)?.color ?? UNKNOWN_ROLE_COLOR;
};

/**
 * Subtitle: the poste (rank) and the contractual weekly volume.
 */
const subtitle = (member: LegacyAccess): string => {
    const schedule = schedules.get(member.id);
    const hours = schedule?.weekly_hours ? ` · ${formatHours(schedule.weekly_hours * 60)}` : '';

    return `${member.rank?.title ?? '—'}${hours}`;
};

const remove = (member: LegacyAccess) => {
    openConfirmationModal(
        "Retirer de l'équipe",
        `Confirmer le retrait de ${member.user?.name ?? 'ce membre'} de votre équipe ?`,
        () => teamApi().deleteAccess(member.id).then(() => emits('refresh'))
    );
};
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <div class="mb-2 flex items-center gap-2 px-1">
      <span class="flex-1 text-xs font-semibold text-gray-500">
        {{ team.length }} {{ pluralize('collaborateur', team.length) }}
      </span>
      <AddTeamMemberButton
        compact
        :team="team"
        :ranks="ranks"
        :operator-code-options="operatorCodeOptions"
        @on-member-added="emits('refresh')"
        @schedule-updated="schedule => emits('schedule-updated', schedule)"
        @on-remove-operator-code="code => emits('on-remove-operator-code', code)"
      />
    </div>
    <div class="flex flex-col gap-1">
      <div
        v-for="member in team"
        :key="member.id"
        class="relative flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 transition-colors"
        :class="member.id === selectedId ? 'bg-primary/10' : 'hover:bg-gray-50'"
        @click="emits('select', member.id)"
      >
        <div
          class="flex size-9 flex-none items-center justify-center rounded-full text-xs font-semibold text-white"
          :style="{backgroundColor: roleColor(member)}"
        >
          {{ initials(member) }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold text-gray-800">
            {{ member.user?.name }}
          </div>
          <div class="truncate text-xs text-gray-400">
            {{ subtitle(member) }}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <button
              class="flex size-6 flex-none items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              @click.stop
            >
              <EllipsisVertical class="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              v-if="member.rank?.title !== 'titulaire'"
              class="text-red-500 focus:text-red-500"
              @click="remove(member)"
            >
              <UserRoundX class="size-4" />
              Retirer de l'équipe
            </DropdownMenuItem>
            <DropdownMenuItem v-else disabled>
              Le titulaire ne peut pas être retiré
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </div>
</template>
